import moment from "moment";
import { companyToday } from "./companyTime";

/**
 * Scorekeeper progressive targets are DAILY ("Memberships · 5 per day").
 * Anchor / rate / whole-month / monthly|weekly reset stay period thresholds.
 */

const num = (v) => {
  const x = Number(v);
  return Number.isFinite(x) && x > 0 ? x : 0;
};

export const rawTargetOf = (def) => {
  if (!def) return 0;
  /* _dailyTarget wins — shaped KPIs overwrite `target` with the period-scaled value. */
  if (num(def._dailyTarget)) return num(def._dailyTarget);
  return (
    num(def.target)
    || num(def.gameConfig?.dailyTarget)
    || num(def.parameters?.dailyTarget)
    || num(def.minimum)
    || 0
  );
};

const resetFreqOf = (def) =>
  def?.parameters?.resetFrequency || def?.gameConfig?.resetFrequency || "";

export const isDailyTarget = (def) => {
  if (!def) return false;
  /* Admin typed an absolute card goal — do not re-scale by window. */
  if (def._fixedTarget) return false;
  if (def.kind === "%") return false;
  if (def.metricType === "anchor" || def.metricType === "display") return false;
  if (def.gameConfig?.type === "anchor") return false;
  const freq = resetFreqOf(def);
  if (freq === "monthly" || freq === "weekly") return false;
  /* Progressive Scorekeeper goals are daily ("Memberships · 5 per day").
     Do NOT block on wholeMonth/calc — those describe how AMOUNTS roll up, and
     mis-tagged sources were freezing Memberships at "of 5" on week/month/year
     while Estimate Won (same progressive type) scaled correctly. */
  if (def.metricType === "progressive") return true;
  if (def.wholeMonth || def.calc === "avg") return false;
  return true;
};

/**
 * How many days a dashboard window covers for progressive targets.
 * Week / month use Mon–Fri business days through today — same pacing as
 * Yeti (`yetiBusinessDayCounts`). Calendar-day stretch counted Sunday and
 * doubled "This week" bars (EWV of $6,000 vs Yeti's $3,000 on a Monday).
 */
const businessDaysInclusive = (from, to) => {
  let n = 0;
  const d = from.clone().startOf("day");
  const end = to.clone().startOf("day");
  if (!d.isValid() || !end.isValid() || end.isBefore(d, "day")) return 1;
  while (d.isSameOrBefore(end, "day")) {
    if (d.day() > 0 && d.day() < 6) n += 1;
    d.add(1, "day");
  }
  return Math.max(1, n);
};

const daysInWindow = (win, custom = null) => {
  const today = companyToday();
  const w = win === "year" ? "ytd" : (win || "month");
  if (w === "today" || w === "yesterday") return 1;
  if (w === "week") {
    /* Sunday-start week — same as resolveWindow / score buckets. */
    return businessDaysInclusive(today.clone().startOf("week"), today);
  }
  if (w === "lastWeek") {
    const start = today.clone().subtract(1, "week").startOf("week");
    return businessDaysInclusive(start, start.clone().endOf("week"));
  }
  if (w === "month") {
    return businessDaysInclusive(today.clone().startOf("month"), today);
  }
  if (w === "lastMonth") {
    const start = today.clone().subtract(1, "month").startOf("month");
    return businessDaysInclusive(start, start.clone().endOf("month"));
  }
  if (w === "quarter") {
    return businessDaysInclusive(today.clone().startOf("quarter"), today);
  }
  if (w === "ytd" || w === "year") {
    return businessDaysInclusive(today.clone().startOf("year"), today);
  }
  if (w === "custom" && custom) {
    const a = custom.from ? moment(custom.from).startOf("day") : today.clone().startOf("month");
    let b = custom.to ? moment(custom.to).startOf("day") : today.clone().startOf("day");
    if (b.isAfter(today, "day")) b = today.clone().startOf("day");
    return businessDaysInclusive(a, b);
  }
  return 1;
};

/**
 * Scale a Scorekeeper daily/period target to the dashboard window.
 * @param {object} def KPI def (metricType, kind, …)
 * @param {number} rawTarget Daily goal for progressive; period threshold otherwise
 * @param {string} kind "$" | "%" | "#"
 * @param {string} win today|yesterday|week|lastWeek|month|lastMonth|quarter|ytd|year|custom
 * @param {{from?:string,to?:string}|null} custom  when win is "custom"
 */
export const stretchTarget = (def, rawTarget, kind, win, custom = null) => {
  const t = Number(rawTarget) || 0;
  if (!t || kind === "%") return t;
  if (!isDailyTarget(def)) return t;
  return t * daysInWindow(win, custom);
};

/** Normalize board/card period strings to a stretch window id. */
export const windowFromPeriod = (period) => {
  if (!period || period === "follow") return "month";
  if (period === "year") return "ytd";
  return period;
};

/**
 * Display target for a metric in a given window.
 * Always scales from `_dailyTarget` (Scorekeeper daily goal) when the metric
 * is progressive — never trust a pre-stretched `target` that may have been
 * skipped or double-applied on some resolve paths (Memberships "of 5" on YTD).
 */
export const displayTargetOf = (metric, kind, period, custom = null) => {
  if (!metric) return 0;
  let k = "#";
  if (kind === "$" || kind === "%") k = kind;
  else if (metric.kind === "$" || metric.kind === "%") k = metric.kind;
  if (k === "%") {
    const t = Number(metric.target) || 0;
    return t > 0 ? t : 1;
  }
  if (metric._fixedTarget) return Number(metric.target) || 0;
  if (!isDailyTarget(metric)) return Number(metric.target) || 0;
  const daily = rawTargetOf(metric);
  return stretchTarget(metric, daily, k, windowFromPeriod(period), custom);
};

export const kindOfKpi = (def, fallback) => {
  if (fallback === "$" || fallback === "%") return fallback;
  if (def?.kind === "$" || def?.kind === "%") return def.kind;
  return "#";
};
